import React, { FC, useMemo, useState } from "react";
import Loader from "@components/Loader/Loader";
import { Box } from "@mui/material";
import { EventInterval, useGetReminderEventsQuery } from "@store/reminderApi";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { LocaleUtil } from "@utils/locale.util";
import Calendar from "@components/Calendar";
import { CalendarEvent } from "@components/Calendar/Calendar";
import { add } from "date-fns";
import { View } from "react-big-calendar";
import { useMediaQuery } from "react-responsive";
import { ScreenSize } from "@constants/screen-size";
import PortalPageWrapper from "@components/Home/PortalPageWrapper";

const mapViewToInterval = (view: View) => {
  switch (view) {
    case "month":
      return EventInterval.Month;
    case "week":
      return EventInterval.Week;
    case "day":
    case "agenda":
    default:
      return EventInterval.Day;
  }
};

const PatientSchedule: FC = () => {
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const isSmScreen = useMediaQuery({
    query: `(max-width: ${ScreenSize.sm}px)`,
  });
  const [date, setDate] = useState<Date>(new Date());
  const [view, setView] = useState<View>(isSmScreen ? "day" : "month");

  const { data: events, isLoading } = useGetReminderEventsQuery(
    {
      patientId: selectedPatientId!,
      tz: LocaleUtil.getTimezone(),
      date: date.toISOString(),
      interval: mapViewToInterval(view),
    },
    {
      skip: !selectedPatientId,
    },
  );

  const calendarEvents: CalendarEvent[] = useMemo(() => {
    if (!events?.data) {
      return [];
    }
    return events.data.map((e) => ({
      title: e.reminder.title,
      start: new Date(e.date),
      end: add(e.date, { hours: 1 }),
    }));
  }, [events]);

  return (
    <PortalPageWrapper titleKey="patientSchedule.title">
      {!events?.data || isLoading ? (
        <Loader />
      ) : (
        <Box flex="1" minHeight="500px">
          <Calendar
            events={calendarEvents}
            date={date}
            onViewChange={setView}
            view={view}
            onNavigate={setDate}
          />
        </Box>
      )}
    </PortalPageWrapper>
  );
};

export default PatientSchedule;
